import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Json, JsonObject } from '@se/cp.common';
import { Logger, SeFormulaParser } from '@se/se.platform.base';
import { ButtonsTypes } from 'estensa-module';
import moment from 'moment';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IEstensaButtonStatus } from '../../models/vnext.models';
import { seToolbarButtonPressed } from '../../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.actions';
import { ToolbarAction, ToolbarButtonData } from '../../se-toolbar/se-buttons-toolbar/types';
import { SsoLoginService } from '../../sso/sso-login.service';
import { ServerRootService } from '../../utils/routing/serverroot.service';
import {
    EstensaBusWrapperService,
    isTpIntegrationAskButtonsBusMessage,
    isTpIntegrationAskStateButtonsStatusBusMessage,
    isTpIntegrationRunBusMessage,
    ITpIntegrationGenericMessage,
    ITpIntegrationStateButtonsBusMessage,
    ITpIntegrationStateButtonsData,
    ITpIntegrationUpdateStateButtonsStatusBusMessage,
} from '../angularjs/estensa-bus-wrapper.service';
import { jsonToIntegrationStoreData } from './routines';
import { IDialogData, StackManagerComponent } from './stack-manager/stack-manager.component';
import { TpIntegrationsCacheService } from './tp-integrations-cache.service';
import {
    EEnableButtonOnSelection,
    EIntegrationType,
    EWhere,
    FullIntegrationStoreData,
    IDisabledConfig,
    IExternalIntegration,
    INotificationMessage,
    IOnCallActionData,
    IRefreshTokenData,
    isRefreshTokenData,
    PairingTokenBundle,
} from './tp-integrations.interface';
import { AuthenticationService } from '../authentication/authentication.service';

interface TpIntegrationsData {
    id: string;
    data: FullIntegrationStoreData;
}

@Injectable({
    providedIn: 'root',
})
export class TpIntegrationsService {
    private readonly actions = inject(Actions);
    private readonly cache = inject(TpIntegrationsCacheService);
    private readonly http = inject(HttpClient);
    private readonly dialog = inject(MatDialog);
    private readonly logger = inject(Logger);
    private readonly serverRootService = inject(ServerRootService);
    private readonly ssoLoginService = inject(SsoLoginService);
    private readonly busService = inject(EstensaBusWrapperService);
    private readonly authenticationService = inject(AuthenticationService);
    static readonly REFRESH_TOKEN_TIMEOUT_DELTA = moment.duration(15, 'seconds');

    // Mappa che associa dati aggiuntivi per ogni azione in esecuzione.
    private readonly runningActions: Record<string, unknown> = {};

    /*decidere se cablarne la location nell'appsettings.json
     * usare comunque un http.get per evitare che venga compilato col bundle
     * @UPDATE il json verra diviso in due parti, una che contiene solo le configurazioni non rischiose
     * type=onCall che rimarra in locale
     * il resto verra messo su db */
    readonly BASE_CONFIG_PATH = 'assets/third-parties-configurations.json';
    config: IExternalIntegration[] = [];
    actionConfig: IExternalIntegration[] = [];
    // forse deve essere inserito un semaforo per inibire la chiamata a due code nello stesso intervallo di tempo. - Finché non è finita una coda non si può elaborare un altra coda
    askData$: Subject<string> = new Subject();
    data$: BehaviorSubject<TpIntegrationsData | null> = new BehaviorSubject<TpIntegrationsData | null>(null); // id della coda
    stackManagerDialog: MatDialogRef<StackManagerComponent> | null = null;

    /*osservabile per avvisare quando il servizio e` operativo a gestire le action di tipo BUTTON*/
    isReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /*osservabile per avvisare quando il servizio e` operativo a gestire le action ON_CALL
     * viene fatta un distinzione tra i tipi di action perche in futuro i button verranno gestiti con json su db
     * mentre le on-call non hanno issue di sicurezza tali per cui possono essere gestite con json nella cartella assets*/
    isActionReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    message$: Subject<INotificationMessage> = new Subject();

    version: string = '1.7.1';

    static readonly createDataFromJsonModel = (modelData: Json) => {
        return jsonToIntegrationStoreData(modelData);
    };

    constructor() {
        this.vNextIntegrationActionSubscription();
        this.vNextIntegrationDataSubcription();
        this.html5IntegrationSubscription();
    }

    onCallAction(actionData: IOnCallActionData) {
        const { action, data } = actionData;
        // c.id === data.actionId mantenuto per retrocompatibilita
        const externalIntegrationData = this.actionConfig.find((c) => c.action === action || c.id === data.actionId);
        if (!externalIntegrationData) {
            throw new Error(`error no service available for action ${action}`);
        }

        this.preParseCalls(externalIntegrationData);

        switch (action) {
            case 'REFRESH_TOKEN':
                if (!isRefreshTokenData(data)) {
                    throw new Error(`error data for refresh token is not in valid format`);
                }
                if (data.isReset) {
                    this.onResetRefreshTokenAction(data);
                } else {
                    this.onRefreshTokenAction(data, Boolean(externalIntegrationData.test));
                }
                break;
            default:
                // store in cache
                this.cache.set(externalIntegrationData.cacheKey || data.actionId || action, data);
                break;
        }
    }

    async init(path = this.BASE_CONFIG_PATH) {
        return this.loadConfigurationData(path);
    }

    html5IntegrationSubscription = (): Subscription =>
        this.busService.busDataFired.subscribe((msg) => {
            if (isTpIntegrationAskButtonsBusMessage(msg)) {
                const { pageName } = msg;
                const buttons = this.config.filter((int) => int.where === pageName).map(this.html5CreateStateButton);
                this.html5NotifyTpIntegrationStateButtons(buttons);
            } else if (isTpIntegrationRunBusMessage(msg)) {
                const {
                    integrationData: { data, id },
                } = msg;
                this.onData(id, jsonToIntegrationStoreData(this.html5ParseData(data)));
            } else if (isTpIntegrationAskStateButtonsStatusBusMessage(msg)) {
                const { pageName, data } = msg;
                const updates = this.html5GetButtonsStatus(
                    pageName,
                    jsonToIntegrationStoreData(this.html5ParseData(data)),
                );

                this.html5NotifyButtonsStatusUpdate(updates);
            }
        });

    html5CreateStateButton = (integration: IExternalIntegration): ITpIntegrationStateButtonsData => ({
        idIntegration: integration.id,
        button: {
            id: integration.id as unknown as ButtonsTypes,
            authorized: false,
            ctxIcon: integration.ctxIcon ?? '',
            disabled: true,
            icon: integration.icon ?? '',
            name: integration.label,
            show: true,
            title: integration.label,
            breaked: integration.breaked,
            onClick: () => {},
        },
    });

    html5NotifyTpIntegrationStateButtons = (buttons: ITpIntegrationStateButtonsData[]): void => {
        const busMessage: ITpIntegrationStateButtonsBusMessage = {
            messageType: 'ESTENSA_TP_INTEGRATION_BUTTONS',
            buttons,
        };
        this.busService.notify(busMessage);
    };

    html5GetButtonsStatus(pageName: string, data: any): { id: string; disabled: boolean }[] {
        return this.config
            .filter((cnf) => cnf.where === pageName)
            .map((cnf) => {
                return { id: cnf.id, disabled: this.html5CheckDisabled(cnf, data) };
            });
    }

    html5NotifyButtonsStatusUpdate = (updates: { id: string; disabled: boolean }[]): void => {
        const busMessage: ITpIntegrationUpdateStateButtonsStatusBusMessage = {
            messageType: 'ESTENSA_TP_INTEGRATION_UPDATE_BUTTONS_STATUS',
            updates,
        };
        this.busService.notify(busMessage);
    };

    private html5Notify = ({ name, data }: INotificationMessage) => {
        const busMessage: ITpIntegrationGenericMessage = {
            messageType: 'ESTENSA_TP_INTEGRATION_GENERIC',
            messageName: name,
            data,
        };
        this.busService.notify(busMessage);
    };

    private html5ParseData = (data: any): JsonObject => {
        if (!data) return {};
        const _data = JSON.parse(JSON.stringify(data));
        if (Array.isArray(_data) && _data.length > 1) return { _selectedItems: _data.map((d) => ({ ...d })) };
        if (Array.isArray(_data) && _data.length === 1)
            return { ..._data[0], _selectedItems: _data.map((d) => ({ ...d })) };
        if (Array.isArray(_data) && _data.length === 0) return { _selectedItems: [] };
        const _userId = this.authenticationService.getUserId();
        return { ..._data, _selectedItems: [{ ..._data }], _userId };
    };

    private html5CheckDisabled(config: IExternalIntegration, data: any): boolean {
        if (config.alwaysEnabled) return false;
        if (config.enableButtonOnSelection) {
            const selected = data._selectedItems?.length ?? 0;
            if (config.enableButtonOnSelection === EEnableButtonOnSelection.SINGLE) return selected !== 1;
            if (config.enableButtonOnSelection === EEnableButtonOnSelection.SINGLE_AND_MULTI) return selected < 1;
            if (config.enableButtonOnSelection === EEnableButtonOnSelection.MULTI) return selected < 2;
        }
        if (config.disabled) return this.parseDisabled(jsonToIntegrationStoreData(data), config.disabled);
        return false;
    }

    vNextIntegrationActionSubscription = (): Subscription =>
        this.actions
            .pipe(
                ofType(seToolbarButtonPressed),
                filter((action) => {
                    return action.button.indexOf(`${ToolbarAction.CALL_THIRD_PARTIES}`) !== -1;
                }),
            )
            .subscribe((action) => {
                const { payload } = action;
                //payload.id e` l'id della callstack da chiamare
                if (!payload?.id) return;
                // emettere dammi i dati.
                this.askData$.next(payload.id);
                // ascolta i dati in data$
            });

    vNextIntegrationDataSubcription = (): Subscription =>
        this.data$.pipe(filter((data) => Boolean(data))).subscribe((intData) => {
            if (intData) {
                const { id, data } = intData;
                this.onData(id, data);
            }
        });

    private vNextNotify = (message: INotificationMessage) => {
        this.message$.next(message);
    };

    private notify = (message: INotificationMessage) => {
        this.vNextNotify(message);
        this.html5Notify(message);
    };

    updateButtonStatus = (where: EWhere, data: any): Partial<Record<ToolbarAction, IEstensaButtonStatus>> => {
        const buttonsConfigs = this.getButtonsConfig(where, data);
        return Object.fromEntries(this.getButtonsKey(where).map((key, i) => [key, buttonsConfigs[i]]));
    };

    compareEstensaButtons(
        buttonsBefore: Partial<Record<ToolbarAction, IEstensaButtonStatus>>,
        buttonsAfter: Partial<Record<ToolbarAction, IEstensaButtonStatus>>,
    ): boolean {
        const buttonsBeforeList = Object.values(buttonsBefore);
        const buttonsAfterList = Object.values(buttonsAfter);
        if (buttonsBeforeList.length !== buttonsAfterList.length) return false;
        return buttonsAfterList.reduce((acc, button, i) => {
            return acc && button.disabled === buttonsBeforeList[i].disabled;
        }, true);
    }

    checkConfigExists(where: EWhere): boolean {
        return this.config.filter((cnf) => cnf.where === where).length > 0;
    }

    mergeButtonsData(buttons: ToolbarButtonData[], where: EWhere): ToolbarButtonData[] {
        const mergedButtons: ToolbarButtonData[] = [];
        const buttonsList = this.makeButtonsData(where);
        buttonsList.sort((a, b) => a[0] - b[0]);
        const integrationsButtonsRecord = Object.fromEntries(buttonsList);
        const L = buttons.length;
        let pos = 0;
        let totalPos = 0;
        while (pos < L) {
            if (integrationsButtonsRecord[totalPos]) {
                mergedButtons.push(integrationsButtonsRecord[totalPos]);
            } else {
                mergedButtons.push(buttons[pos]);
                pos++;
            }
            totalPos++;
        }

        const outOfRangeButtons = buttonsList.filter((button) => button[0] >= L);
        mergedButtons.push(...outOfRangeButtons.map((button) => button[1]));
        return mergedButtons;
    }

    parseDisabled(dataStore: any, disabledData: IDisabledConfig): boolean {
        const { formula, placeholders } = disabledData;
        const formulaParser = new SeFormulaParser();
        placeholders.forEach((p: string, i: number) => formulaParser.setVariable(`B_${i}`, dataStore[p] ?? null));
        const formulaResult = formulaParser.parse(formula);
        if (formulaResult.error) {
            this.logger.error(`Error calculating parseDisabled formula: ${formulaResult.error}`);

            return true; //in caso di errore si forza la disabilitazione
        }
        return Boolean(formulaResult.result);
    }

    private onData(id: string, data: FullIntegrationStoreData) {
        console.log(
            `%cThird parties integration service version: %c${this.version}`,
            'color:#2a9d8f; padding:2px;',
            'background-color:#264653; color:white; font-size:110%; border-radius:3px; padding:2px;',
        );

        //inizia chiamata della pila di esecuzione.
        const externalIntegrationData = this.config.find((c) => c.id === id);
        if (!externalIntegrationData) throw new Error(`error no configuration available for id ${id}`);
        this.preParseCalls(externalIntegrationData);

        if (externalIntegrationData.constants && typeof externalIntegrationData.constants === 'object') {
            jsonToIntegrationStoreData(externalIntegrationData.constants, '__constants', data);
        }

        const dialogData: IDialogData = {
            id,
            data,
            externalIntegrationData,
            onNotify: this.notify,
        };

        this.stackManagerDialog = this.dialog.open(StackManagerComponent, {
            disableClose: true,
            width: '100vw',
            height: '100vh',
            data: dialogData,
            panelClass: 'tp-dialog-container',
        });

        this.stackManagerDialog.afterClosed().subscribe((_) => {
            this.stackManagerDialog = null;
            this.data$.next(null);
        });
    }

    private preParseCalls(integrationData: IExternalIntegration) {
        if (integrationData.test && integrationData.calls?.length) {
            integrationData.calls.forEach((call) => {
                call.test = true;
            });
        }
    }

    /**************************** TOKEN SECTION ****************************/

    private getServerRoot(test = false): string {
        const baseUri = this.serverRootService.getBaseRoot();
        return test ? `${baseUri}/test` : baseUri;
    }

    private onRefreshTokenAction(data: IRefreshTokenData, test = false) {
        const { actionId, refreshUri, cacheKey, cacheInitialValue } = data;
        if (cacheInitialValue) {
            this.cache.set(cacheKey, cacheInitialValue);
        }
        this.runningActions[data.actionId] = this.refreshToken(actionId, cacheKey, refreshUri, test);
    }

    private refreshToken(actionId: string, cacheKey: string, refreshUri: string, test = false) {
        const token = this.cache.get(cacheKey) as PairingTokenBundle;
        const uri = `${this.getServerRoot(test)}${refreshUri}`;
        const refreshTimeout = moment
            .duration(token.expires_in || 5, 'seconds')
            .subtract(TpIntegrationsService.REFRESH_TOKEN_TIMEOUT_DELTA)
            .asMilliseconds();

        return window.setTimeout(async () => {
            this.logger.debug(`Refreshing SSO token: ${JSON.stringify(token)} with ${uri}`);
            try {
                const res = await firstValueFrom(this.http.post<PairingTokenBundle>(uri, token));
                if (this.runningActions[actionId] === null) {
                    // È possibile che l'azione sia stata cancellata.
                    return;
                }
                const { access_token, expires_in, refresh_token, id_token } = res;

                this.logger.debug(`SSO Token refreshed`);

                this.cache.set(cacheKey, { access_token, expires_in, refresh_token, id_token });
                this.ssoLoginService.setOpenIdToken(id_token);
                this.runningActions[actionId] = this.refreshToken(actionId, cacheKey, refreshUri);
            } catch (err: any) {
                this.logger.error(`Error while refreshing SSO token: ${err.message}`);

                this.runningActions[actionId] = null;
                this.cache.set(cacheKey, null);
                this.ssoLoginService.removeOpenIdToken();
                throw err;
            }
        }, refreshTimeout);
    }

    private onResetRefreshTokenAction = (data: IRefreshTokenData) => {
        const actionData = this.runningActions[data.actionId];
        if (typeof actionData === 'number') {
            clearTimeout(actionData);
        }
        this.runningActions[data.actionId] = null;
    };

    /********************* END TOKEN SECTION ****************/

    private async loadConfigurationData(path = this.BASE_CONFIG_PATH) {
        try {
            const allConfig = await firstValueFrom(this.http.get<IExternalIntegration[]>(path));
            /*!== ON_CALL per retrocompatibilita, altrimenti i vecchi json che non hanno type=BUTTON rimuovono i buttons*/
            this.config = allConfig.filter(
                (cnf) => cnf.type !== EIntegrationType.ON_CALL /*=== EIntegrationType.BUTTON*/,
            );
            this.actionConfig = allConfig.filter(
                (cnf) => cnf.type === EIntegrationType.ON_CALL && (cnf.action || cnf.id),
            );
            this.isReady$.next(true);
            this.isActionReady$.next(true);
            return true;
        } catch (err: any) {
            this.logger.error(`Error loading configuration data: ${err.message}`);
        }
    }

    private getButtonsKey(where: EWhere): ToolbarAction[] {
        return this.config
            .filter((cnf) => cnf.where === where)
            .map((cnf) => {
                const { id } = cnf;
                return `${ToolbarAction.CALL_THIRD_PARTIES}_${id}` as ToolbarAction;
            });
    }

    private getButtonsConfig(where: EWhere, data: any): IEstensaButtonStatus[] {
        let forceDisabled: boolean = false;
        if (!data || typeof data !== 'object') forceDisabled = true; // throw error too!!!!
        const dataStore = jsonToIntegrationStoreData(data);
        return this.config
            .filter((cnf) => cnf.where === where)
            .map((cnf) => {
                const { disabled, alwaysEnabled, enableButtonOnSelection } = cnf;

                const selectedExaminationsCount = data?._selectedExaminations?.length || 0;
                forceDisabled =
                    (enableButtonOnSelection === EEnableButtonOnSelection.SINGLE && selectedExaminationsCount !== 1) ||
                    (enableButtonOnSelection === EEnableButtonOnSelection.MULTI && selectedExaminationsCount < 2) ||
                    (!!enableButtonOnSelection &&
                        [EEnableButtonOnSelection.MULTI, EEnableButtonOnSelection.SINGLE_AND_MULTI].includes(
                            enableButtonOnSelection,
                        ) &&
                        selectedExaminationsCount === 0);

                const config = { visible: true, disabled: false };
                if (forceDisabled) config.disabled = true;
                else if (disabled) config.disabled = this.parseDisabled(dataStore, disabled);
                /* alwaysEnabled ha priorita su tutto*/
                if (alwaysEnabled) config.disabled = false;
                return config;
            });
    }

    private makeButtonsData(where: EWhere): [number, ToolbarButtonData][] {
        return this.config
            .filter((cnf) => cnf.where === where)
            .map((cnf) => {
                const { label, description, id } = cnf;
                let position = cnf.position ?? -1;
                position = position >= 0 ? position : Infinity;
                return [
                    position,
                    {
                        label,
                        permission: ButtonsTypes.externalLinks,
                        isToggle: false,
                        hasChildren: false,
                        status: { visible: true, disabled: false, checked: false },
                        payload: { id, description },
                        actionType: `${ToolbarAction.CALL_THIRD_PARTIES}_${id}` as ToolbarAction,
                    },
                ];
            });
    }
}
